import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Loader2, Mail, MessageCircle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buildVendorSupportLinks, SUPPORT_CONFIG } from "@/config/support";

export default function VendorSupportPage() {
  const { vendor, role } = useAuth();
  const links = buildVendorSupportLinks({ vendor, role });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      setTickets(await api.listVendorSupportTickets());
    } catch (error) {
      toast.error(error.message || "Could not load support requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      const ticket = await api.createVendorSupportTicket({ subject, message });
      setTickets((current) => [ticket, ...current]);
      setSubject("");
      setMessage("");
      toast.success("Support request sent. We’ll follow up here or through your contact channel.");
    } catch (error) {
      toast.error(error.message || "Could not send your support request.");
    } finally {
      setSending(false);
    }
  };

  return <div className="max-w-3xl space-y-6" data-testid="vendor-support-page">
    <div>
      <p className="text-xs uppercase tracking-widest text-brand">Help &amp; Support</p>
      <h1 className="mt-2 font-display text-3xl">Talk to {SUPPORT_CONFIG.brandName}</h1>
      <p className="mt-3 text-ink-secondary">Send a tracked support request, or use WhatsApp and email for a direct conversation.</p>
    </div>

    <form onSubmit={submit} className="card-surface p-4 sm:p-6" data-testid="vendor-support-form">
      <div className="flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-lg border border-brand/30 bg-brand-soft"><LifeBuoy className="h-5 w-5 text-brand" /></div><div><h2 className="font-medium">Send a support request</h2><p className="text-sm text-ink-secondary">Your request is securely linked to this signed-in workspace.</p></div></div>
      <label className="mt-5 block text-xs uppercase tracking-widest text-ink-muted" htmlFor="support-subject">Subject</label>
      <input id="support-subject" required maxLength="160" value={subject} onChange={(event) => setSubject(event.target.value)} className="input-field mt-2" placeholder="What do you need help with?" data-testid="vendor-support-subject" />
      <label className="mt-4 block text-xs uppercase tracking-widest text-ink-muted" htmlFor="support-message">Details</label>
      <textarea id="support-message" required maxLength="2000" value={message} onChange={(event) => setMessage(event.target.value)} className="input-field mt-2 min-h-28 resize-y" placeholder="Include the steps you took and what you expected to happen." data-testid="vendor-support-message" />
      <button type="submit" disabled={sending} className="btn-primary mt-4 inline-flex min-h-11 items-center gap-2 disabled:opacity-60" data-testid="vendor-support-submit">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
      </button>
    </form>

    <div className="grid gap-4 sm:grid-cols-2">
      <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" className="card-surface min-h-[132px] p-5 flex flex-col justify-between focus-visible:ring-2 focus-visible:ring-brand" data-testid="vendor-support-whatsapp"><MessageCircle className="h-6 w-6 text-brand" /><span className="mt-4 font-medium">WhatsApp support</span></a>
      <a href={links.email} className="card-surface min-h-[132px] p-5 flex flex-col justify-between focus-visible:ring-2 focus-visible:ring-brand" data-testid="vendor-support-email"><Mail className="h-6 w-6 text-brand" /><span className="mt-4 font-medium">Email support</span></a>
    </div>

    <section className="card-surface p-4 sm:p-6" data-testid="vendor-support-history">
      <div className="flex items-center justify-between gap-3"><div><h2 className="font-medium">Your support requests</h2><p className="mt-1 text-sm text-ink-secondary">Only requests created by your account are shown.</p></div><button onClick={loadTickets} className="btn-ghost min-h-11" disabled={loading}>Refresh</button></div>
      {loading ? <div className="py-8 text-center text-ink-secondary"><Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /></div> : tickets.length === 0 ? <p className="py-6 text-sm text-ink-secondary">No support requests yet.</p> : <ul className="mt-4 divide-y divide-line">{tickets.map((ticket) => <li key={ticket.id} className="py-4 first:pt-0" data-testid={`vendor-support-ticket-${ticket.id}`}><div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="font-medium">{ticket.subject}</div><p className="mt-1 whitespace-pre-wrap text-sm text-ink-secondary">{ticket.message}</p><div className="mt-2 text-xs text-ink-muted">{new Date(ticket.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div></div><span className="rounded-full border border-brand/30 bg-brand-soft px-2 py-0.5 text-[10px] uppercase tracking-widest text-brand">{ticket.status.replace("_", " ")}</span></div></li>)}</ul>}
    </section>
  </div>;
}
